import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';
import { scrollIntoView } from '@theme/scrolling';
import { ZoomMediaSelectedEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';
/**
 * A custom element that renders a zoom dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement[]} media - The media elements.
 * @property {HTMLElement} thumbnails - The thumbnails elements.
 *
 * @extends Component<Refs>
 */
export class ZoomDialog extends Component {
  requiredRefs = ['dialog', 'media', 'thumbnails'];

  #highResImagesLoaded = /** @type {WeakSet<HTMLImageElement>} */ (new WeakSet());
  #highResRequests = /** @type {WeakMap<HTMLImageElement, HTMLImageElement>} */ (new WeakMap());
  #activeIndex = 0;
  #sourceIndex = 0;
  #pointerStartedOutside = false;
  #opener = /** @type {HTMLElement | null} */ (null);

  connectedCallback() {
    super.connectedCallback();
    this.refs.dialog.addEventListener('load', this.#handleImageLoad, true);
    this.refs.dialog.addEventListener('close', this.#restoreFocus);
    this.refs.dialog.addEventListener('pointerdown', this.#handlePointerDown);
    this.refs.dialog.addEventListener('click', this.#handleOutsideClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.dialog.removeEventListener('load', this.#handleImageLoad, true);
    this.refs.dialog.removeEventListener('close', this.#restoreFocus);
    this.refs.dialog.removeEventListener('pointerdown', this.#handlePointerDown);
    this.refs.dialog.removeEventListener('click', this.#handleOutsideClick);
  }

  /**
   * Opens the zoom dialog.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  open(index, event) {
    event.preventDefault();
    const { dialog, media } = this.refs;
    if (dialog.open || !media[index]) return;

    this.#opener = event.target instanceof Element ? event.target.closest('button') : null;
    this.#sourceIndex = index;
    this.#pointerStartedOutside = false;
    // Never hold an interaction behind a document-wide snapshot/animation.
    this.#preparePreview(index);
    this.#activateMedia(index);
    dialog.showModal();
    this.selectThumbnail(index, { behavior: 'instant' });
  }

  /** Reuse an already loaded image while the larger zoom image downloads.
   * @param {number} index
   */
  #preparePreview(index) {
    const media = this.refs.media[index];
    if (!media?.classList.contains('product-media-container--image')) return;
    const image = media.querySelector('img.product-media__image');
    if (!(image instanceof HTMLImageElement) || (image.complete && image.naturalWidth)) return;

    const gallery = /** @type {import('./media-gallery').MediaGallery | null} */ (
      this.closest('media-gallery')
    );
    const sources = [
      gallery?.slideshow?.slides?.[index]?.querySelector('img.product-media__image'),
      gallery?.media?.[index]?.querySelector('img.product-media__image'),
      this.refs.thumbnails?.children[index]?.querySelector('img'),
    ];
    const preview = sources.find(
      (source) =>
        source instanceof HTMLImageElement &&
        source.complete &&
        source.naturalWidth &&
        source.currentSrc
    );
    if (preview instanceof HTMLImageElement) {
      image.removeAttribute('srcset');
      image.removeAttribute('sizes');
      image.src = preview.currentSrc;
    }
    // The selected image must load even if there is no cached gallery fallback.
    image.loading = 'eager';
  }

  /** Upgrade only after download and decode; failures leave the preview intact.
   * @param {HTMLElement} mediaContainer
   */
  loadHighResolutionImage(mediaContainer) {
    if (!mediaContainer.classList.contains('product-media-container--image')) return false;
    const image = mediaContainer.querySelector('img.product-media__image');
    if (!(image instanceof HTMLImageElement)) return false;
    if (this.#highResImagesLoaded.has(image) || this.#highResRequests.has(image)) return false;

    const highResolutionUrl =
      image.getAttribute('data_max_resolution') || image.getAttribute('data-max-resolution');
    if (!highResolutionUrl) return false;

    if (image.complete && image.naturalWidth) {
      try {
        if (
          new URL(image.currentSrc || image.src, image.baseURI).href ===
          new URL(highResolutionUrl, image.baseURI).href
        ) {
          this.#highResImagesLoaded.add(image);
          return false;
        }
      } catch {
        // A malformed source follows the normal error path without hiding the preview.
      }
    }

    // Preserve alt text, focal point, dimensions and any merchant image attributes.
    const newImage = /** @type {HTMLImageElement} */ (image.cloneNode(false));
    newImage.removeAttribute('srcset');
    newImage.removeAttribute('sizes');
    newImage.loading = 'eager';
    newImage.decoding = 'async';
    newImage.fetchPriority = 'auto';
    this.#highResRequests.set(image, newImage);
    const clearRequest = () => {
      this.#highResRequests.delete(image);
      newImage.onload = null;
      newImage.onerror = null;
    };
    newImage.onerror = clearRequest;
    newImage.onload = async () => {
      try {
        await newImage.decode();
        // Section replacement or a newer image must not be overwritten by a late load.
        if (!this.isConnected || mediaContainer.querySelector('img.product-media__image') !== image)
          return;
        image.replaceWith(newImage);
        this.#highResImagesLoaded.add(newImage);
        if (this.refs.media[this.#activeIndex] === mediaContainer) this.#fitMedia();
      } catch {
        // Keep the usable preview; another interaction can retry the upgrade.
      } finally {
        clearRequest();
      }
    };
    newImage.src = highResolutionUrl;
    return true;
  }

  /** Size the stage from intrinsic media dimensions, never the constrained render box. */
  #fitMedia() {
    const media = this.refs.media[this.#activeIndex];
    const image = media?.querySelector('img');
    const width = image?.naturalWidth || Number(image?.getAttribute('width'));
    const height = image?.naturalHeight || Number(image?.getAttribute('height'));
    const ratio = width > 0 && height > 0 ? width / height : 1;
    this.refs.dialog.style.setProperty('--zoom-media-ratio', String(ratio));
  }

  /** @param {number} index */
  #activateMedia(index) {
    this.#activeIndex = index;
    // Only the selected media occupies the stage. Resizing cannot shift a scroll
    // offset into another image, and inactive controls stay out of the Tab order.
    this.refs.media.forEach((media, position) => {
      media.hidden = position !== index;
    });
    this.#fitMedia();
  }

  /** @param {Event} event */
  #handleImageLoad = (event) => {
    if (
      this.refs.dialog.open &&
      event.target instanceof Node &&
      this.refs.media[this.#activeIndex]?.contains(event.target)
    ) {
      this.#fitMedia();
    }
  };

  /** @param {EventTarget | null} target */
  #isOutsideContent(target) {
    return target instanceof Element && !target.closest('.product-media-container, button');
  }

  /** @param {PointerEvent} event */
  #handlePointerDown = (event) => {
    this.#pointerStartedOutside = this.#isOutsideContent(event.target);
  };

  /** @param {MouseEvent} event */
  #handleOutsideClick = (event) => {
    const dismiss = this.#pointerStartedOutside && this.#isOutsideContent(event.target);
    this.#pointerStartedOutside = false;
    if (dismiss) this.close();
  };

  /**
   * Closes the zoom dialog.
   */
  close() {
    this.closeDialog();
  }

  closeDialog() {
    const { dialog } = this.refs;
    if (!dialog.open) return;
    dialog.close();
    window.dispatchEvent(new DialogCloseEvent());
  }

  #restoreFocus = () => {
    // Native close events are queued. A rapid reopen must keep focus in the new modal.
    if (this.refs.dialog.open) return;
    // Zoom selection is independent; return to the original storefront trigger.
    const opener = this.#opener;
    const gallery = /** @type {import('./media-gallery').MediaGallery | null} */ (
      this.closest('media-gallery')
    );
    const candidates = [
      opener,
      gallery?.querySelector(
        'slideshow-slide[aria-hidden="false"] .product-media-container__zoom-button'
      ),
      gallery?.media?.[this.#sourceIndex]?.querySelector('.product-media-container__zoom-button'),
    ];
    const target = candidates.find(
      (element) =>
        element?.isConnected &&
        element.getClientRects().length &&
        !element.closest('[aria-hidden="true"]')
    );
    if (target instanceof HTMLElement) target.focus({ preventScroll: true });
    this.#opener = null;
  };

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.close();
  }

  /**
   * Handles the click event of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   */
  async handleThumbnailClick(index) {
    const behavior = prefersReducedMotion() ? 'instant' : 'smooth';
    this.selectThumbnail(index, { behavior });
  }

  /**
   * Handles the pointer enter event of a thumbnail.
   * @param {number} index - The index of the thumbnail to load the high-resolution image for.
   */
  async handleThumbnailPointerEnter(index) {
    const { media } = this.refs;
    if (!media[index]) return;

    this.loadHighResolutionImage(media[index]);
  }

  /**
   * Handles the selection of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   * @param {Object} options - The options for the selection.
   * @param {ScrollBehavior} options.behavior - The behavior of the scroll.
   */
  async selectThumbnail(
    index,
    options = { behavior: prefersReducedMotion() ? 'instant' : 'smooth' }
  ) {
    const { media, thumbnails } = this.refs;
    if (!Number.isInteger(index) || !media[index]) return;
    this.#preparePreview(index);
    this.#activateMedia(index);
    this.loadHighResolutionImage(media[index]);

    const targetThumbnail = thumbnails?.children[index];
    if (targetThumbnail instanceof HTMLElement) {
      Array.from(thumbnails.querySelectorAll('button')).forEach((button, i) => {
        button.setAttribute('aria-current', `${i === index}`);
      });
      scrollIntoView(targetThumbnail, {
        ancestor: thumbnails,
        behavior: options.behavior,
        block: 'center',
        inline: 'center',
      });
    }
    this.dispatchEvent(new ZoomMediaSelectedEvent(index));
  }
}

if (!customElements.get('zoom-dialog')) {
  customElements.define('zoom-dialog', ZoomDialog);
}
